import type { PropsWithChildren } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ONBOARDING_CONST } from '@/constant';
import { useTypedSearchParams } from '@/hook';
import { VButton, VInput, VSelect, VStack, useUnleash } from '@/package';
import { useCognitoStore, useTenantStore } from '@/store';
import type { OnboardingForm, OnboardingSearchParams } from '@/type';

import { PrivacyAgree } from '../Auth';

const Tenant = () => {
  const { t } = useTranslation();
  const {
    register,
    setValue,
    watch,
    formState: { touchedFields },
  } = useFormContext<OnboardingForm>();
  const { getVariant } = useUnleash();
  const { set, flush } = useTypedSearchParams<OnboardingSearchParams>();
  const { getRegionList } = useCognitoStore();
  const { isExistTenant } = useTenantStore();
  const [regionList, setRegionList] = useState([] as string[]);
  const [errorMap, setErrorMap] = useState({
    companyName: '',
    jobTitle: '',
    region: '',
    tenantName: '',
    userName: '',
  } as {
    [K in keyof Pick<
      OnboardingForm,
      'companyName' | 'jobTitle' | 'region' | 'tenantName' | 'userName'
    >]: string;
  });
  const [isTenantValidated, setIsTenantValidated] = useState(false);
  const enabledUserAgreementFlag =
    getVariant('vuex.cordoba.userAgreement')?.feature_enabled ?? false;
  const isTenantInputFormVarified = useMemo(
    () =>
      !!watch('jobTitle') &&
      !!watch('region') &&
      !!watch('tenantName') &&
      !!watch('userName') &&
      watch('termsOfService') &&
      watch('personalInfoCollection') &&
      watch('personalInfoTransfer'),
    [
      watch('jobTitle'),
      watch('region'),
      watch('tenantName'),
      watch('userName'),
      watch('personalInfoCollection'),
      watch('personalInfoTransfer'),
      watch('termsOfService'),
    ],
  );

  const toBeErrorMessage = t('auth.duplicatedCompanyNameErrorMessage.drierman');

  const setError = <K extends keyof typeof errorMap>(
    key: K,
    errorMessage: string,
  ) => {
    setErrorMap(prev => ({ ...prev, [key]: errorMessage }));
  };

  const validateTenantName = () => {
    if (!touchedFields.tenantName) return true;
    return /^([A-Za-z0-9][-A-Za-z0-9_.]*)?[A-Za-z0-9]$/.test(
      watch('tenantName') ?? '',
    );
  };

  const verfiyTenantName = () => {
    const tenantName = watch('tenantName');

    return isExistTenant(tenantName ?? '');
  };

  const validateFormField = <
    K extends keyof Pick<OnboardingForm, 'jobTitle' | 'region' | 'userName'>,
  >(
    key: K,
  ) => {
    if (!touchedFields[key]) return true;

    const value = watch(key)?.trim() ?? '';

    return value.length > 0;
  };

  const onContinueButtonClick = () => {
    let hasError = false;

    if (!watch('region')?.trim() || !validateFormField('region')) {
      setError('region', t('auth.requiredRegionErrorMessage.haisla'));
      hasError = true;
    } else {
      setError('region', '');
    }

    if (!watch('tenantName')?.trim() || !validateTenantName()) {
      setError('tenantName', t('auth.invalidOrgNameErrorMessage.disconnecter'));
      hasError = true;
    } else {
      setError('tenantName', '');
    }

    if (!watch('jobTitle')?.trim() || !validateFormField('jobTitle')) {
      setError('jobTitle', t('auth.requiredJobTitleErrorMessage.dissolute'));
      hasError = true;
    } else {
      setError('jobTitle', '');
    }

    if (!watch('userName')?.trim() || !validateFormField('userName')) {
      setError('userName', t('auth.requiredYourNameErrorMessage.distortion'));
      hasError = true;
    } else {
      setError('userName', '');
    }

    if (hasError) {
      return;
    }

    if (!isTenantValidated) {
      verfiyTenantName().then(isExist => {
        if (isExist === null) {
          setError('tenantName', t('error.defaultError.coperiodic'));
          return;
        }

        if (isExist) {
          setError('tenantName', toBeErrorMessage);
          return;
        }

        setIsTenantValidated(true);
        setError('tenantName', '');
        set('step', ONBOARDING_CONST.ONBOARDING_STEP_QUERY_PARAM.CHOOSE_PLAN);
        flush();
      });
    }
  };

  useEffect(() => {
    if (!enabledUserAgreementFlag) {
      setValue('termsOfService', true);
      setValue('marketingPromotion', false);
      setValue('personalInfoCollection', true);
      setValue('personalInfoTransfer', true);
      setValue('consentAITraining', false);
    }
  }, [enabledUserAgreementFlag]);

  useEffect(() => {
    getRegionList().then(setRegionList);
  }, []);

  return (
    <div className="flex flex-col gap-4 pt-6">
      <InputGroup title={t('auth.selectionRegionTitle.guttus')}>
        {/** NOTE: VSelect register 의 onChange 가 제대로 동작하지 않아 강제로 setValue 적용 */}
        {/** TODO: VSelect register 의 onChange 가 왜 동작하지 않는지 확인 필요함 */}
        <VSelect
          {...register('region')}
          label="Region"
          itemList={regionList.map(region => ({ value: region }))}
          required
          fluid
          onChange={e => setValue('region', e.target.value)}
        />
        {errorMap.region && (
          <span className="flex w-[296px] justify-start text-body-md text-error-500">
            {errorMap.region}
          </span>
        )}
      </InputGroup>

      <InputGroup title={t('auth.workspaceInfo.desirousness')}>
        <VInput
          {...register('tenantName')}
          label="Workspace name"
          required
          fluid
        />
        {errorMap.tenantName && (
          <span className="flex w-[296px] justify-start text-body-md text-error-500">
            {errorMap.tenantName}
          </span>
        )}
      </InputGroup>

      <InputGroup title={t('auth.companyInfo.dethroner')}>
        <VStack gap={1} className="w-full">
          <VInput
            {...register('companyName')}
            label={t('auth.companyNameLabel.dipody')}
            required
            fluid
          />
          {errorMap.companyName && (
            <span className="flex w-[296px] justify-start text-body-md text-error-500">
              {errorMap.companyName}
            </span>
          )}
        </VStack>

        <VStack gap={1} className="w-full">
          <VInput
            {...register('jobTitle')}
            label={t('auth.jobTitleLabel.disbarment')}
            required
            fluid
          />
          {errorMap.jobTitle && (
            <span className="flex w-[296px] justify-start text-body-md text-error-500">
              {errorMap.jobTitle}
            </span>
          )}
        </VStack>

        <VStack gap={1} className="w-full">
          <VInput
            {...register('userName')}
            label={t('auth.yourNameLabel.disciplelike')}
            required
            fluid
          />
          {errorMap.userName && (
            <span className="flex w-[296px] justify-start text-body-md text-error-500">
              {errorMap.userName}
            </span>
          )}
        </VStack>
      </InputGroup>

      {enabledUserAgreementFlag && (
        <div className="border-y border-gray-300 py-4">
          <PrivacyAgree
            onChangeAgree={termItemList => {
              termItemList.forEach(termItem => {
                setValue(
                  termItem.name as keyof OnboardingForm,
                  termItem.checked,
                );
              });
            }}
          />
        </div>
      )}

      <VButton
        disabled={!isTenantInputFormVarified}
        type="button"
        onClick={onContinueButtonClick}
      >
        {t('common.continueButtonText.hipparion')}
      </VButton>
    </div>
  );
};

const InputGroup = ({
  title,
  children,
}: PropsWithChildren<{
  title: string;
}>) => {
  return (
    <VStack gap={1} className="w-[296px]">
      <div className="text-body-xl font-bold">{title}</div>
      {children}
    </VStack>
  );
};

export default Tenant;
